import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, StyleSheet, TextInput, TouchableOpacity, Vibration, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Shadow, Spacing } from '../../theme';
import ReplyPreview from './ReplyPreview';
import QuickActionsRow from './QuickActionsRow';
import { useTheme } from '../../theme/useTheme';
import type { TonyteTheme } from '../../theme/themes';
import { daylightStyle } from '../../theme/daylightStyle';

interface ReplyTarget {
  senderName: string;
  text: string;
}

// Derived from the input's own typography (fontSize 14 below) rather than a
// flat pixel guess — one compact line, growing up to ~6 lines before the
// TextInput becomes internally scrollable (RN does this automatically once
// its content exceeds a fixed `height` on a multiline input).
const INPUT_FONT_SIZE = 14;
const INPUT_LINE_HEIGHT = Math.round(INPUT_FONT_SIZE * 1.3);
const INPUT_VERTICAL_PADDING = 10;
const MIN_INPUT_HEIGHT = 40;
const MAX_INPUT_LINES = 6;
const MAX_INPUT_HEIGHT = INPUT_LINE_HEIGHT * MAX_INPUT_LINES + INPUT_VERTICAL_PADDING * 2;

interface Props {
  value: string;
  onChangeText: (text: string) => void;
  onSend: () => void;
  onTyping?: () => void;
  disabled?: boolean;
  placeholder: string;
  sending?: boolean;
  replyTarget?: ReplyTarget | null;
  onDismissReply?: () => void;
  onPickPhoto: () => void;
  onTakePhoto: () => void;
  onShareLocation: () => void;
  onGif: () => void;
  onVoice: () => void;
}

const ChatInputBar: React.FC<Props> = ({
  value,
  onChangeText,
  onSend,
  onTyping,
  disabled,
  placeholder,
  sending,
  replyTarget,
  onDismissReply,
  onPickPhoto,
  onTakePhoto,
  onShareLocation,
  onGif,
  onVoice,
}) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createAppearanceStyles(theme), [theme]);
  const [focused, setFocused] = useState(false);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const [inputHeight, setInputHeight] = useState(MIN_INPUT_HEIGHT);
  const sendScale = useRef(new Animated.Value(1)).current;

  const canSend = Boolean(value.trim()) && !disabled && !sending;

  // Reset to the compact single-line height once the message is cleared —
  // covers both a successful send (parent clears `value`) and the user
  // manually deleting all their text.
  useEffect(() => {
    if (!value) setInputHeight(MIN_INPUT_HEIGHT);
  }, [value]);

  const handleChangeText = useCallback((text: string) => {
    onChangeText(text);
    onTyping?.();
  }, [onChangeText, onTyping]);

  const handleContentSizeChange = useCallback((event: { nativeEvent: { contentSize: { height: number } } }) => {
    setInputHeight(Math.min(MAX_INPUT_HEIGHT, Math.max(MIN_INPUT_HEIGHT, event.nativeEvent.contentSize.height)));
  }, []);

  const handleSendPressIn = useCallback(() => {
    Animated.spring(sendScale, { toValue: 0.86, useNativeDriver: true, speed: 30, bounciness: 0 }).start();
  }, [sendScale]);
  const handleSendPressOut = useCallback(() => {
    Animated.spring(sendScale, { toValue: 1, useNativeDriver: true, speed: 16, bounciness: 10 }).start();
  }, [sendScale]);
  const handleSend = useCallback(() => {
    if (!canSend) return;
    try {
      Vibration.vibrate(10);
    } catch {
      // best-effort only
    }
    onSend();
  }, [canSend, onSend]);

  return (
    <View style={styles.wrap}>
      {replyTarget ? (
        <ReplyPreview senderName={replyTarget.senderName} text={replyTarget.text} onDismiss={onDismissReply} />
      ) : null}

      <View style={[styles.row, appearance.row]}>
        <LinearGradient
          pointerEvents="none"
          colors={theme.isDark
            ? ['rgba(255,255,255,0.092)', 'rgba(255,255,255,0.032)', 'rgba(242,125,38,0.018)']
            : theme.gradients.navBase}
          locations={[0, 0.62, 1]}
          start={{ x: 0.08, y: 0 }}
          end={{ x: 0.92, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        {!theme.isDark ? (
          <>
            <LinearGradient
              pointerEvents="none"
              colors={theme.gradients.navGlass}
              locations={[0, 0.5, 1]}
              start={{ x: 0.08, y: 0 }}
              end={{ x: 0.72, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
            <LinearGradient
              pointerEvents="none"
              colors={theme.gradients.navWarm}
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 0.5 }}
              style={StyleSheet.absoluteFill}
            />
            <View pointerEvents="none" style={styles.pearlTopHighlight} />
            <View pointerEvents="none" style={styles.pearlBottomRim} />
          </>
        ) : null}
        <TouchableOpacity
          style={[
            styles.expandBtn,
            appearance.expandBtn,
            actionsExpanded && appearance.expandBtnActive,
          ]}
          activeOpacity={0.8}
          onPress={() => setActionsExpanded(prev => !prev)}
        >
          {!theme.isDark ? (
            <LinearGradient
              pointerEvents="none"
              colors={actionsExpanded ? theme.gradients.navButtonActive : theme.gradients.navButtonInactive}
              start={{ x: 0.2, y: 0 }}
              end={{ x: 0.8, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
          ) : null}
          <Icon
            name={actionsExpanded ? 'x' : 'plus'}
            size={18}
            color={theme.isDark
              ? 'rgba(255,255,255,0.75)'
              : actionsExpanded ? theme.colors.accent : theme.colors.icon}
          />
        </TouchableOpacity>

        <QuickActionsRow
          expanded={actionsExpanded}
          onPickPhoto={() => { setActionsExpanded(false); onPickPhoto(); }}
          onTakePhoto={() => { setActionsExpanded(false); onTakePhoto(); }}
          onShareLocation={() => { setActionsExpanded(false); onShareLocation(); }}
          onGif={() => { setActionsExpanded(false); onGif(); }}
          onVoice={() => { setActionsExpanded(false); onVoice(); }}
        />

        {!actionsExpanded ? (
          <View style={[styles.inputGlass, appearance.inputGlass, focused && styles.inputGlassFocused, focused && appearance.inputGlassFocused]}>
            <LinearGradient
              pointerEvents="none"
              colors={theme.isDark ? ['rgba(255,255,255,0.08)', 'rgba(255,255,255,0.02)'] : theme.gradients.surface}
              start={{ x: 0.1, y: 0 }}
              end={{ x: 0.9, y: 1 }}
              style={styles.inputGlow}
            />
            <TextInput
              style={[styles.input, appearance.input, { height: inputHeight }]}
              value={value}
              onChangeText={handleChangeText}
              placeholder={placeholder}
              placeholderTextColor={theme.isDark ? 'rgba(255,255,255,0.32)' : theme.colors.textMuted}
              multiline
              textAlignVertical="top"
              onContentSizeChange={handleContentSizeChange}
              editable={!disabled}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
            />
          </View>
        ) : null}

        <Animated.View style={{ transform: [{ scale: sendScale }] }}>
          <TouchableOpacity
            style={[styles.sendBtn, appearance.sendBtn, !canSend && styles.sendBtnDisabled]}
            onPress={handleSend}
            onPressIn={handleSendPressIn}
            onPressOut={handleSendPressOut}
            disabled={!canSend}
            activeOpacity={0.9}
          >
            {canSend ? (
              <LinearGradient
                pointerEvents="none"
                colors={['#FFA968', Colors.sunset, '#D9640F']}
                start={{ x: 0.15, y: 0 }}
                end={{ x: 0.9, y: 1 }}
                style={StyleSheet.absoluteFill}
              />
            ) : null}
            <Icon name="send" size={18} color={canSend || theme.isDark ? '#fff' : theme.colors.iconMuted} />
          </TouchableOpacity>
        </Animated.View>
      </View>
    </View>
  );
};

export default ChatInputBar;

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: Spacing.base,
    paddingTop: Spacing.sm,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.38,
    shadowRadius: 22,
    elevation: 14,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 28,
    padding: 5,
    backgroundColor: 'rgba(6,10,18,0.64)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
  },
  expandBtn: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.075)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.17)',
    overflow: 'hidden',
  },
  inputGlass: {
    flex: 1,
    minHeight: MIN_INPUT_HEIGHT,
    maxHeight: MAX_INPUT_HEIGHT,
    borderRadius: Radius.xxl,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    backgroundColor: 'rgba(255,255,255,0.058)',
    overflow: 'hidden',
    justifyContent: 'center',
  },
  inputGlassFocused: {
    borderColor: 'rgba(242,125,38,0.55)',
    backgroundColor: 'rgba(255,255,255,0.07)',
  },
  inputGlow: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
  },
  input: {
    color: '#fff',
    fontSize: INPUT_FONT_SIZE,
    lineHeight: INPUT_LINE_HEIGHT,
    paddingHorizontal: 16,
    paddingVertical: INPUT_VERTICAL_PADDING,
    maxHeight: MAX_INPUT_HEIGHT,
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: 'rgba(255,255,255,0.09)',
    ...Shadow.glow,
    shadowOpacity: 0.28,
    shadowRadius: 18,
  },
  sendBtnDisabled: {
    shadowOpacity: 0,
  },
  pearlTopHighlight: {
    position: 'absolute',
    top: 0,
    left: 24,
    right: 24,
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.90)',
  },
  pearlBottomRim: {
    position: 'absolute',
    left: 18,
    right: 18,
    bottom: 0,
    height: 1,
    backgroundColor: 'rgba(137,105,76,0.18)',
  },
});

const createAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  row: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceGlass,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.navigation,
  }),
  expandBtn: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.card,
  }),
  expandBtnActive: daylightStyle(theme, {
    borderColor: 'rgba(242,125,38,0.46)',
    shadowColor: theme.colors.accent,
    shadowOpacity: 0.18,
    shadowRadius: 9,
    shadowOffset: { width: 0, height: 3 },
    elevation: 4,
  }),
  inputGlass: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.border,
  }),
  inputGlassFocused: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.accent,
  }),
  input: daylightStyle(theme, {
    color: theme.colors.textPrimary,
  }),
  sendBtn: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
  }),
});
